import { Box, Flex, IconButton, Divider, Tooltip } from '@chakra-ui/react';
import { Editor } from '@tiptap/react';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdFormatStrikethrough,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdCode,
  MdHorizontalRule,
  MdImage,
  MdLink,
  MdUndo,
  MdRedo,
} from 'react-icons/md';

interface TipTapToolbarProps {
  editor: Editor | null;
  onImageUpload: () => void;
}

export default function TipTapToolbar({ editor, onImageUpload }: TipTapToolbarProps) {
  if (!editor) return null;

  const ToolbarButton = ({
    icon,
    label,
    isActive,
    onClick,
  }: {
    icon: React.ReactElement;
    label: string;
    isActive?: boolean;
    onClick: () => void;
  }) => (
    <Tooltip label={label} hasArrow>
      <IconButton
        aria-label={label}
        icon={icon}
        size="sm"
        variant={isActive ? 'solid' : 'ghost'}
        colorScheme={isActive ? 'brand' : 'gray'}
        onClick={onClick}
      />
    </Tooltip>
  );

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Box
      borderBottom="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      borderTopRadius="md"
      px={2}
      py={1}
    >
      <Flex wrap="wrap" gap={0.5} align="center">
        <ToolbarButton icon={<MdFormatBold />} label="Bold" isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton icon={<MdFormatItalic />} label="Italic" isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton icon={<MdFormatUnderlined />} label="Underline" isActive={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarButton icon={<MdFormatStrikethrough />} label="Strikethrough" isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />

        <Divider orientation="vertical" h="24px" mx={1} />

        <ToolbarButton icon={<>H1</>} label="Heading 1" isActive={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} />
        <ToolbarButton icon={<>H2</>} label="Heading 2" isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton icon={<>H3</>} label="Heading 3" isActive={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />

        <Divider orientation="vertical" h="24px" mx={1} />

        <ToolbarButton icon={<MdFormatListBulleted />} label="Bullet List" isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton icon={<MdFormatListNumbered />} label="Ordered List" isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolbarButton icon={<MdFormatQuote />} label="Blockquote" isActive={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <ToolbarButton icon={<MdCode />} label="Code Block" isActive={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />

        <Divider orientation="vertical" h="24px" mx={1} />

        <ToolbarButton icon={<MdLink />} label="Link" isActive={editor.isActive('link')} onClick={addLink} />
        <ToolbarButton icon={<MdImage />} label="Image" onClick={onImageUpload} />
        <ToolbarButton icon={<MdHorizontalRule />} label="Horizontal Rule" onClick={() => editor.chain().focus().setHorizontalRule().run()} />

        <Divider orientation="vertical" h="24px" mx={1} />

        <ToolbarButton icon={<MdUndo />} label="Undo" onClick={() => editor.chain().focus().undo().run()} />
        <ToolbarButton icon={<MdRedo />} label="Redo" onClick={() => editor.chain().focus().redo().run()} />
      </Flex>
    </Box>
  );
}
